import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import "./styles.css";
import { useQuery } from "react-query";
import { getFundingTrends } from "ui-io/queries/get-funding-trends";
import { FundingTrendT } from "ui-io/company-io";
import { formatLargeNumber } from "ui-lib";
import { useMemo } from "react";
import { Button, Colors, Spinner } from "@blueprintjs/core";
import { styled } from "styled-components";
import {
  NoData,
  CustomCard,
  TitleComponent,
  useDarkMode,
  CustomButton,
} from "ui-components";
import { MarketCapT } from "ui-io/common-io";

type ChartArea = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

const verticalCrosshairPlugin = {
  id: "verticalCrosshair",
  afterDraw: (chart: any) => {
    const tooltip = chart.tooltip;

    if (tooltip && tooltip._active && tooltip._active.length) {
      const ctx = chart.ctx;
      const activePoint = tooltip._active[0];
      const x = activePoint.element.x;

      const topY = chart.chartArea.top;
      const bottomY = chart.chartArea.bottom;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#888";
      ctx.setLineDash([4, 2]);
      ctx.stroke();
      ctx.restore();
    }
  },
};

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  verticalCrosshairPlugin
);

// Function to get gradient for the chart
function getGradient(ctx: CanvasRenderingContext2D, chartArea: ChartArea) {
  let gradient = ctx.createLinearGradient(
    0,
    chartArea.bottom,
    0,
    chartArea.top
  );
  gradient.addColorStop(0.9, "#8A2BE2");
  gradient.addColorStop(0, "#DAB2FF");
  return gradient;
}
const getGraphData = (graphData: number[], graphLabel: string[]) => {
  return {
    labels: graphLabel,
    datasets: [
      {
        data: graphData,
        fill: true,
        backgroundColor: function (context: {
          chart: { ctx: CanvasRenderingContext2D; chartArea: ChartArea };
        }) {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          // This case happens on initial chart load
          if (!chartArea) return;
          return getGradient(ctx, chartArea);
        },
        borderRadius: 20,
        barThickness: 40,
        borderSkipped: false,
        tension: 0.3,
      },
    ],
  };
};

type Props = {
  year?: string;
  title?: string;
  graphData?: number[];
  graphLabel?: string[];
  isValueSymbol?: boolean;
	marketCapital?:MarketCapT;
};
const currentYear = new Date().getFullYear();

const WeeklyOverview = (props: Props) => {
  const [darkMode] = useDarkMode();
  const { year = currentYear, title = "", isValueSymbol = false,marketCapital } = props;

  const connection = useQuery(getFundingTrends({ year: Number(year),marketCapital }));

  const { graphData, graphLabel }: any = useMemo(() => {
    if (!connection?.data) {
      return {};
    }

    const data = connection.data;
    return {
      graphData: data.map((i: FundingTrendT) => i.totalAmount),
      graphLabel: data.map((i: FundingTrendT) => i.monthLabel),
    };
  }, [connection.data]);
  
  const formatLabel = (() => {
    let lastLabel = "";
    return (label: string, totalLabels: number): string => {
      const date = new Date(label);
      const year = date.getFullYear();
      const quarter = `Q${Math.floor(date.getMonth() / 3) + 1}`;

      let currentLabel = "";
      if (totalLabels < 24) {
        currentLabel = label;
      } else if (totalLabels <= 48) {
        currentLabel = `${quarter} ${year}`;
      } else {
        currentLabel = `${year}`;
      }

      if (currentLabel === lastLabel) return ""; 
      lastLabel = currentLabel;
      return currentLabel;
    };
  })();

  // Define options for the line chart
  const options: ChartOptions<"line"> = {
    responsive: true,

    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.raw;
            return formatLargeNumber(value as number);
          },
        },
      },
    },
    elements: {
      line: { borderWidth: 2 },
      point: { radius: 0, hoverRadius: 0 },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          color: darkMode ? Colors.GRAY4 : Colors.GRAY2,
          callback: function (value, index, ticks) {
            const rawLabel = this.getLabelForValue(value as number);
            const totalLabels = ticks.length;
            if (index === 0) return formatLabel(rawLabel, totalLabels);
            if (
              index > 0 &&
              rawLabel === this.getLabelForValue(ticks[index - 1].value)
            ) {
              return "";
            }
            return formatLabel(rawLabel, totalLabels);
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        ticks: {
          color: darkMode ? Colors.GRAY4 : Colors.GRAY2,
          callback: function (value: any) {
            return isValueSymbol ? `£${value}Bn` : formatLargeNumber(value);
          },
        },
      },
    },
  };

  return (
    <CustomCard marginTop="25px">
      <div className="display-flex justify-between align-center mb-md">
        <TitleComponent fontSize="24px">
          {title || "Funding trends"}
        </TitleComponent>
        <CustomButton bradius="20px" rightIcon="chevron-right">
          More
        </CustomButton>
      </div>
      <div className="bar-chart-container">
        {connection.isFetching && <CustomSpinner size={70} />}

        {connection?.data?.length ? (
          <Line data={getGraphData(graphData, graphLabel)} options={options} />
        ) : (
          !connection.isFetching && <NoData />
        )}
      </div>
    </CustomCard>
  );
};

export default WeeklyOverview;

const CustomSpinner = styled(Spinner)<{ tableview?: boolean }>`
  top: ${(props) => (props.tableview ? "0" : "35%")};
  left: ${(props) => (props.tableview ? "0" : "47.5%")};
  position: ${(props) => (props.tableview ? "relative" : "absolute")};
`;
